import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accountHierarchy } from "../lib/accountHierarchy";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  ErrorState,
  Field,
  Modal,
  Money,
  PageHeader,
  Select,
  Spinner,
  TextField,
} from "../components/ui";
import { ImportModal } from "../components/ImportModal";
import { StatementsModal } from "../components/StatementsModal";
import { ACCOUNT_TYPE_LABELS } from "../lib/format";
import type { Account } from "../lib/models";
import type { AccountType } from "@shared/types";

/** Bank-style accounts a statement can be imported into. */
const IMPORTABLE_TYPES: AccountType[] = ["asset", "liability"];

const TYPE_ORDER: AccountType[] = [
  "asset",
  "liability",
  "equity",
  "income",
  "expense",
];

export default function ChartOfAccounts() {
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [importAccount, setImportAccount] = useState<Account | null>(null);
  const [statementsAccount, setStatementsAccount] = useState<Account | null>(null);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const currency = currentOrg?.baseCurrency ?? "USD";

  const { data, isLoading, error } = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
    enabled: !!currentOrg,
  });

  const grouped = (data ?? []).reduce<Record<string, Account[]>>((acc, a) => {
    (acc[a.type] ??= []).push(a);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        title="Chart of Accounts"
        subtitle="Your ledger's account structure"
        action={<Button onClick={() => setOpen(true)}>New account</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <div className="space-y-6">
          {TYPE_ORDER.filter((t) => grouped[t]?.length).map((type) => (
            <div key={type}>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                {ACCOUNT_TYPE_LABELS[type]}
              </h2>
              <div className="card overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="th w-24">Code</th>
                      <th className="th">Name</th>
                      <th className="th">Detail type</th>
                      <th className="th text-right">Balance</th>
                      <th className="th text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {accountHierarchy(grouped[type]).map(({ account:a,depth,path }) => (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className="td font-mono text-gray-500">{a.code ?? "—"}</td>
                        <td className="td font-medium text-gray-900" style={{paddingLeft:16+depth*22}} title={path}>
                          {depth>0&&<span className="mr-2 text-gray-400" aria-hidden="true">↳</span>}{a.name}
                          {a.systemKey && (
                            <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-400">
                              system
                            </span>
                          )}
                        </td>
                        <td className="td text-gray-500">
                          {a.subtype?.replaceAll("_", " ") ?? "—"}
                        </td>
                        <td className="td text-right">
                          <Money cents={a.currentBalance} currency={currency} />
                        </td>
                        <td className="td text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              className="px-2.5 py-1 text-xs"
                              onClick={() => setEditAccount(a)}
                            >
                              Edit
                            </Button>
                            {IMPORTABLE_TYPES.includes(a.type) && (
                              <>
                                <Button
                                  variant="secondary"
                                  className="px-2.5 py-1 text-xs"
                                  onClick={() => setImportAccount(a)}
                                >
                                  Import CSV
                                </Button>
                                <Button
                                  variant="secondary"
                                  className="px-2.5 py-1 text-xs"
                                  onClick={() => setStatementsAccount(a)}
                                >
                                  Statements
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <NewAccountModal
        accounts={data ?? []}
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          qc.invalidateQueries({ queryKey: ["accounts"] });
        }}
      />

      {importAccount && (
        <ImportModal
          account={importAccount}
          currency={currency}
          onClose={() => setImportAccount(null)}
        />
      )}

      {statementsAccount && (
        <StatementsModal
          account={statementsAccount}
          currency={currency}
          onClose={() => setStatementsAccount(null)}
        />
      )}

      {editAccount && (
        <EditAccountModal
          account={editAccount}
          onClose={() => setEditAccount(null)}
          onSaved={() => {
            setEditAccount(null);
            qc.invalidateQueries({ queryKey: ["accounts"] });
          }}
        />
      )}
    </div>
  );
}

function EditAccountModal({
  account,
  onClose,
  onSaved,
}: {
  account: Account;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    code: account.code ?? "",
    name: account.name,
    subtype: account.subtype ?? "",
    description: account.description ?? "",
    bankName: account.bankName ?? "",
    bankAccountNumber: account.bankAccountNumber ?? "",
  });
  const isBank = IMPORTABLE_TYPES.includes(account.type);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/accounts/${account.id}`, {
        code: form.code || null,
        name: form.name,
        subtype: form.subtype || null,
        description: form.description || null,
        ...(isBank
          ? {
              bankName: form.bankName || null,
              bankAccountNumber: form.bankAccountNumber || null,
            }
          : {}),
      }),
    onSuccess: onSaved,
  });

  return (
    <Modal open onClose={onClose} title={`Edit ${account.name}`}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <Field label="Account code">
            <TextField
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </Field>
          <Field label="Detail type">
            <TextField
              value={form.subtype}
              onChange={(e) => setForm({ ...form, subtype: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Name">
          <TextField
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Description">
          <TextField
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        {isBank && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Bank name">
              <TextField
                value={form.bankName}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                placeholder="Wells Fargo"
              />
            </Field>
            <Field label="Account number">
              <TextField
                value={form.bankAccountNumber}
                onChange={(e) =>
                  setForm({ ...form, bankAccountNumber: e.target.value })
                }
                placeholder="••••1234"
              />
            </Field>
          </div>
        )}
        {save.error && <ErrorState error={save.error} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function NewAccountModal({
  accounts,
  open,
  onClose,
  onCreated,
}: {
  accounts: Account[];
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    type: "expense" as AccountType,
    parentId: "",
    subtype: "",
    description: "",
  });
  const create = useMutation({
    mutationFn: () =>
      api.post("/accounts", {
        code: form.code || undefined,
        name: form.name,
        type: form.type,
        parentId: form.parentId || undefined,
        subtype: form.subtype || undefined,
        description: form.description || undefined,
      }),
    onSuccess: onCreated,
  });

  return (
    <Modal open={open} onClose={onClose} title="New account">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <Field label="Account code">
            <TextField
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="6000"
            />
          </Field>
          <Field required label="Type">
            <Select
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as AccountType, parentId: "" })
              }
            >
              {TYPE_ORDER.map((t) => (
                <option key={t} value={t}>
                  {ACCOUNT_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Name">
          <TextField
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Parent account (optional)">
          <Select value={form.parentId} onChange={e=>setForm({...form,parentId:e.target.value})}>
            <option value="">No parent — top level</option>
            {accountHierarchy(accounts.filter(a=>a.type===form.type&&a.isActive)).map(({account,path})=>(<option key={account.id} value={account.id}>{account.code ? account.code+' · ' : ''}{path}</option>))}
          </Select>
        </Field>
        <Field label="Detail type (optional)">
          <TextField
            value={form.subtype}
            onChange={(e) => setForm({ ...form, subtype: e.target.value })}
            placeholder="office_expenses"
          />
        </Field>
        {create.error && <ErrorState error={create.error} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Create account"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
